import type { BaseEntityState, ServerListParams } from '@crm/shared';

// Re-export dei tipi polimorfici (vivono nelle proprie cartelle entita'
// dedicate). Qui re-esportiamo per retrocompatibilita' con i file che
// le importavano da businessTypes.
export type { EmailAddress } from '../emailAddress/emailAddressTypes';
export type { Phone } from '../phone/phoneTypes';
export type { Address } from '../address/addressTypes';

// Fonte di verita' dei types Business: vivono in businessMappers (accanto
// al DTO REST e ai mapper int<->string). Qui li re-esportiamo per
// mantenere l'import path storico usato da tutto il codebase UI.
export type { Business, BusinessStatus } from './businessMappers';

import type { Business, BusinessStatus } from './businessMappers';

// Costanti UI con label i18n key. Il compilatore verifica che `value`
// sia uno dei membri del corrispondente union type.
export const BUSINESS_STATUSES: { value: BusinessStatus; label: string }[] = [
  { value: 'attivo', label: 'business.statuses.attivo' },
  { value: 'inattivo', label: 'business.statuses.inattivo' },
];

/** Parametri di lista dell'azienda: quelli comuni piu' la bacheca. */
export interface BusinessListParams extends ServerListParams {
  /**
   * Se valorizzato, restituisce le aziende della bacheca indicata, cioe' quelle collocate in uno
   * degli stati della sua pipeline. Ha precedenza sugli altri filtri.
   */
  kanbanId?: number;
}

export interface BusinessState extends BaseEntityState<Business> {
  /** Totale records disponibili lato server (per pagination DataGrid). */
  totalCount: number;
}

export interface BusinessCreatePayload {
  externalCode?: string;
  name: string;
  domain: string;
  owner?: string;
  sector?: string;
  type?: string;
  employees?: string;
  vatNumber?: string;
  fiscalCode?: string;
  sdiCode?: string;
  pecEmail?: string;
  /**
   * Form-only fields per quick-add di email/telefono/indirizzo dalla
   * form di create/edit. NON serializzati nel POST/PUT /business — il
   * mapper ignora questi campi. La persistenza viene gestita da
   * `BusinessForm.handleSubmit` con dispatch separato di
   * `addEmailAddress`/`addPhone`/`addAddress` dopo la create/update.
   *
   * Semantica (uniforme per email/phone/address):
   *   - in CREATE diventano l'unico email/phone/address (primary=true);
   *   - in EDIT vengono aggiunti ai preesistenti (primary=false).
   *
   * `addressCountryId` / `addressRegionId` sono i lookup ID dei
   * Country/Region Liferay (vedi `entities/geography` per la cache)
   * — passati nel payload `addAddress` come `countryId`/`regionId`.
   */
  email?: string;
  phone?: string;
  addressName?: string;
  addressDescription?: string;
  addressStreet?: string;
  addressCity?: string;
  addressZip?: string;
  addressCountryId?: number | null;
  addressRegionId?: number | null;
}

export interface BusinessUpdatePayload extends Partial<BusinessCreatePayload> {
  businessId: number;
  /**
   * Colonna della bacheca in cui spostare l'azienda (0 = fuori dalla board).
   *
   * Sta qui e NON in `BusinessCreatePayload`: dalla form di creazione non si sceglie una colonna, e
   * l'unico chiamante è il drag&drop della bacheca, che aggiorna un'azienda esistente. Il contratto
   * REST lo accetta anche in POST, ma finché nessuno lo invia il tipo non lo promette.
   */
  currentStateId?: number;
}

/**
 * Valori di default del form Business (create). Vive qui, non in `BusinessFormBody`,
 * così quel file esporta SOLO il componente (react-refresh/fast-refresh) e la costante
 * resta condivisibile da `BusinessForm` per il merge dei valori di prefill.
 */
export const BUSINESS_FORM_DEFAULTS: BusinessCreatePayload = {
  externalCode: '',
  name: '', domain: '', owner: '', sector: '', type: '', employees: '',
  vatNumber: '', fiscalCode: '', sdiCode: '', pecEmail: '',
  email: '', phone: '',
  addressName: '', addressDescription: '', addressStreet: '', addressCity: '', addressZip: '',
  addressCountryId: null, addressRegionId: null,
};
